import React, { useState } from "react";
import {
    Button,
    Box,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    List,
    ListItem,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import * as z from "zod";
import { zodResolver, displayError } from "../utils/form";
import { FormattedMessage, useIntl } from "react-intl";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import TitledDialog from "./TitledDialog";
import Loading from "./Loading";
import PasswordField from "./PasswordField";
import { styled } from "@mui/material/styles";

const _LABEL_NAME_PREFIX = "ldap_test_form" as const;

const FormContainer = styled("form")(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const ResultContainer = styled(Paper)(({ theme }) => ({
    marginTop: theme.spacing(2),
    overflow: "hidden",
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    maxHeight: 440,
}));

const StyledTable = styled(Table)(({ theme }) => ({
    minWidth: 500,
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
    padding: theme.spacing(0.5, 0),
}));

const SubTable = styled(Table)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

function useValidationSchema() {
    const { formatMessage } = useIntl();

    return z
        .object({
            username: z.string().min(1, formatMessage({ id: "required_error" })),
            password: z.string().min(1, formatMessage({ id: "required_error" })),
            timeout: z.number(),
        })
        .required();
}

type FormInputs = z.infer<ReturnType<typeof useValidationSchema>>;

const LdapTestFormDialog: React.FC<{
    open: boolean;
    serverData: Record<string, any>;
    hide: () => void;
}> = ({ open, serverData, hide }) => {
    const schema = useValidationSchema();

    const [result, setResult] = useState(undefined);
    const [fetching, setFetching] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<FormInputs>({
        resolver: zodResolver(schema),
        defaultValues: {
            username: "",
            password: "",
            timeout: 3,
        },
        mode: "onChange",
    });

    const onSubmit: SubmitHandler<FormInputs> = async (input) => {
        const data = {
            account: input,
            server: serverData,
        };

        setFetching(true);
        setResult(undefined);

        const request = new Request("/api/test-ldap-connection", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
        });

        try {
            const response = await fetch(request);

            setFetching(false);
            if (response.ok) {
                setResult(await response.json());
            }
        } catch (error) {
            setFetching(false);
            console.error(error);
        }
    };

    return (
        <TitledDialog
            open={open}
            title={`${_LABEL_NAME_PREFIX}.caption`}
            isContentChanged={() => false}
            hide={hide}
            actions={
                <Button variant="contained" onClick={handleSubmit(onSubmit)}>
                    <FormattedMessage id={`${_LABEL_NAME_PREFIX}.send`} />
                </Button>
            }
        >
            <FormContainer noValidate onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={4}>
                        <Typography variant="body1">
                            <FormattedMessage id="ldap_test_form.username" />
                        </Typography>
                    </Grid>
                    <Grid size={8}>
                        <Controller
                            control={control}
                            name="username"
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    size="small"
                                    error={!!errors.username}
                                    helperText={errors.username ? displayError(errors.username) : null}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Typography variant="body1">
                            <FormattedMessage id="ldap_test_form.password" />
                        </Typography>
                    </Grid>
                    <Grid size={8}>
                        <Controller
                            control={control}
                            name="password"
                            render={({ field }) => (
                                <PasswordField
                                    {...field}
                                    autoComplete="new-password"
                                    size="small"
                                    error={!!errors.password}
                                    helperText={errors.password ? displayError(errors.password) : null}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Typography variant="body1">
                            <FormattedMessage id="ldap_test_form.timeout" />
                        </Typography>
                    </Grid>
                    <Grid size={8}>
                        <Controller
                            control={control}
                            name="timeout"
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    onChange={(e) => {
                                        const target = e.target as HTMLInputElement;
                                        field.onChange(Number(target.value));
                                    }}
                                    type="number"
                                    slotProps={{
                                        input: {
                                            inputProps: { step: 1 },
                                        },
                                    }}
                                    size="small"
                                    fullWidth
                                    error={!!errors.timeout}
                                    helperText={errors.timeout ? displayError(errors.timeout) : null}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </FormContainer>
            {fetching ? <Loading /> : result && <LdapTestResultContent result={result} />}
        </TitledDialog>
    );
};

const LdapTestResultContent = ({ result }: { result: any | undefined }) => {
    if (!result) {
        return null;
    }

    return (
        <ResultContainer>
            <StyledTableContainer>
                <StyledTable aria-label="LDAP test results">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <FormattedMessage id={`${_LABEL_NAME_PREFIX}.host`} />
                            </TableCell>
                            <TableCell>
                                <FormattedMessage id={`${_LABEL_NAME_PREFIX}.bind`} />
                            </TableCell>
                            <TableCell>
                                <FormattedMessage id={`${_LABEL_NAME_PREFIX}.message`} />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <LdapTestResultRows result={result} />
                    </TableBody>
                </StyledTable>
            </StyledTableContainer>
        </ResultContainer>
    );
};

const LdapTestResultRows = ({ result }: { result: any }) => {
    const _Label = ({ name }: { name: string }) => <FormattedMessage id={`${_LABEL_NAME_PREFIX}.${name}`} />;

    const _Message = ({ server }: { server: any }) => {
        if (!server.success) return server.message;

        return (
            <Box>
                <Typography variant="subtitle2">
                    <_Label name="membership" />
                </Typography>
                <List dense disablePadding>
                    {server.message.membership.map((name: string, index: number) => (
                        <StyledListItem key={index}>{name}</StyledListItem>
                    ))}
                </List>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    <_Label name="user_info" />
                </Typography>
                <SubTable size="small">
                    <TableBody>
                        {Object.entries(server.message["user_info"]).map((e, index) => (
                            <TableRow key={index}>
                                <TableCell component="th" scope="row">
                                    <_Label name={e[0]} />
                                </TableCell>
                                <TableCell>{e[1]}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </SubTable>
            </Box>
        );
    };

    const _Row = ({ server }: { server: any }) => {
        return (
            <TableRow>
                <TableCell>{server.host}</TableCell>
                <TableCell>
                    <FontAwesomeIcon
                        icon={server.success ? faCheck : faTimes}
                        color={server.success ? "green" : "red"}
                    />
                </TableCell>
                <TableCell>
                    <_Message server={server} />
                </TableCell>
            </TableRow>
        );
    };

    return (
        <>
            {result.map((server: any, index: number) => (
                <_Row key={index} server={server} />
            ))}
        </>
    );
};

export default LdapTestFormDialog;
